// Lists that page instead of scrolling. A list marked data-fit="<id>" shows as many of its items as fit
// in the page's area, with a small pager under it; the page itself never scrolls (Cole, 2026-10-27).
// It runs after each render of the page and on resize. Items in a grid break into pages at row edges.

const pageOf = new Map<string, number>();
const sizeOf = new Map<string, number>();
/** What each list held when last paged (its size and first item): a new sort or filter goes back to page 1. */
const sigOf = new Map<string, string>();

/** The area that must not scroll: the page element. */
const PAGER_H = 40;

export function fitLists(root: HTMLElement | null) {
  if (!root) return;
  // A list marked both ways pages sideways while it is a row, else down (a row on a wide screen, a column on a phone).
  const sideways = new Set<HTMLElement>();
  for (const list of root.querySelectorAll<HTMLElement>('[data-fit-x]')) if (fitRow(list)) sideways.add(list);
  const lists = [...root.querySelectorAll<HTMLElement>('[data-fit]')].filter((l) => !sideways.has(l));
  if (!lists.length) return;
  const page = root.closest<HTMLElement>('#page') ?? root;
  for (const list of lists) fitOne(list, page);
}

/**
 * A row marked data-fit-x="<id>" (a track of equal columns) shows as many columns as fit its width, with a pager
 * under it, instead of scrolling sideways. It opens on the page holding its item marked data-fit-start.
 */
function fitRow(list: HTMLElement): boolean {
  const id = list.dataset.fitX!;
  const items = [...list.children].filter((c): c is HTMLElement => c instanceof HTMLElement);
  for (const el of items) el.style.display = '';
  let pager = list.nextElementSibling as HTMLElement | null;
  if (!pager?.classList.contains('fit-pager')) pager = null;
  if (pager) pager.hidden = true;
  if (!list.offsetParent || items.length < 2 || list.scrollWidth <= list.clientWidth + 1) return false;
  const cs = getComputedStyle(list);
  const gap = parseFloat(cs.columnGap) || 0;
  const room = list.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
  // Pages fill the width greedily: columns may differ in width (milestones are wider).
  const starts: number[] = [0];
  let used = 0;
  items.forEach((el, i) => {
    const w = el.getBoundingClientRect().width;
    if (i > starts[starts.length - 1] && used + w > room + 0.5) { starts.push(i); used = 0; }
    used += w + gap;
  });
  const pages = starts.length;
  const pageAt = (i: number) => { let p = 0; while (p + 1 < pages && starts[p + 1] <= i) p++; return p; };
  const sig = `${items.length}:${starts.join(',')}`;
  if (sigOf.get(id) !== sig) {
    sigOf.set(id, sig);
    const at = items.findIndex((el) => el.hasAttribute('data-fit-start'));
    pageOf.set(id, at > 0 ? pageAt(at) : 0);
  }
  const p = Math.min(pageOf.get(id) ?? 0, pages - 1);
  pageOf.set(id, p);
  const from = starts[p], to = p + 1 < pages ? starts[p + 1] : items.length;
  items.forEach((el, i) => { if (i < from || i >= to) el.style.display = 'none'; });
  list.scrollLeft = 0;
  if (!pager) { pager = document.createElement('div'); pager.className = 'fit-pager'; pager.dataset.key = `fitpager-${id}`; list.after(pager); }
  pager.hidden = false;
  pager.innerHTML = pagerHtml(id, p, pages);
  return true;
}

function pagerHtml(id: string, p: number, pages: number) {
  return `<button class="btn small" data-fit-go="${id}:${p - 1}"${p ? '' : ' disabled'} aria-label="Previous page">‹</button>`
    + `<span class="fit-n">${p + 1} / ${pages}</span>`
    + `<button class="btn small" data-fit-go="${id}:${p + 1}"${p + 1 < pages ? '' : ' disabled'} aria-label="Next page">›</button>`;
}

function fitOne(list: HTMLElement, page: HTMLElement, extra = 0, tries = 0, before?: { p: number; size?: number }) {
  const id = list.dataset.fit!;
  const all = [...list.children].filter((c): c is HTMLElement => c instanceof HTMLElement && !c.classList.contains('fit-pager'));
  for (const el of all) el.style.display = '';
  // Items the stylesheet hides (rows shown on a podium above) take no part in paging.
  const items = all.filter((el) => el.getClientRects().length > 0);
  const sig = `${items.length}:${items[0]?.dataset.key ?? ''}`;
  if (sigOf.get(id) !== sig) { sigOf.set(id, sig); pageOf.delete(id); }
  // A table's rows page with the pager after the table (or its scroll box), never inside it.
  const anchor = list.tagName === 'TBODY' ? (list.closest<HTMLElement>('.gscroll') ?? list.closest<HTMLElement>('table') ?? list) : list;
  let pager = anchor.nextElementSibling as HTMLElement | null;
  if (!pager?.classList.contains('fit-pager')) pager = null;
  if (pager) pager.hidden = true;
  if (!list.offsetParent || items.length < 2) return;
  const focus = focusOf.get(id);
  if (!tries) focusOf.delete(id);
  // The page box scrolls on a phone; on wider screens the document itself would.
  const de = document.documentElement;
  const over = Math.max(page.scrollHeight - page.clientHeight, de.scrollHeight - de.clientHeight);
  if (over <= 1) { pageOf.delete(id); return; }
  const top = list.getBoundingClientRect().top;
  const room = list.getBoundingClientRect().height - over - PAGER_H - extra;
  // Page breaks: a new page starts at the first item whose bottom would pass the room.
  const starts: number[] = [0];
  let start = items[0].getBoundingClientRect().top - top;
  for (let i = 0; i < items.length; i++) {
    const r = items[i].getBoundingClientRect();
    const bottom = r.bottom - top - start;
    if (bottom > room && i > starts[starts.length - 1]) {
      // The whole row moves to the next page: back up to the first item on this item's row.
      let j = i;
      while (j > starts[starts.length - 1] && Math.abs(items[j - 1].getBoundingClientRect().top - r.top) < 2) j--;
      if (j === starts[starts.length - 1]) j = i;
      starts.push(j);
      start = items[j].getBoundingClientRect().top - top;
    }
  }
  const pages = starts.length;
  if (pages < 2) return;
  // A changed page size (a resize, a new filter) keeps the first item in view where it can. Measured against the page
  // and size before this fit: the first pass (no pager yet) can fit one item more than the final one, and mapping the
  // page through that in-between size sent page 2 back to page 1.
  const prev = before ?? { p: pageOf.get(id) ?? 0, size: sizeOf.get(id) };
  let p = Math.min(prev.p, pages - 1);
  if (prev.size && prev.size !== starts[1]) p = Math.min(pages - 1, Math.floor((prev.p * prev.size) / starts[1]));
  const at = focus ? items.findIndex((el) => el.matches(focus)) : -1;
  if (at >= 0) { p = 0; while (p + 1 < pages && starts[p + 1] <= at) p++; }
  pageOf.set(id, p); sizeOf.set(id, starts[1]);
  const from = starts[p], to = p + 1 < pages ? starts[p + 1] : items.length;
  items.forEach((el, i) => { if (i < from || i >= to) el.style.display = 'none'; });
  if (!pager) { pager = document.createElement("div"); pager.className = "fit-pager"; pager.dataset.key = `fitpager-${id}`; anchor.after(pager); }
  pager.hidden = false;
  pager.dataset.fitFor = id;
  pager.innerHTML = pagerHtml(id, p, pages);
  // The pager (or gaps around it) can take more room than allowed for: fit again with that much less.
  const left = Math.max(page.scrollHeight - page.clientHeight, de.scrollHeight - de.clientHeight);
  if (left > 1 && to - from > 1 && tries < 3) {
    if (at >= 0 && focus) focusOf.set(id, focus);
    // At least the page's last item moves on; more if the page is still too tall.
    const lastH = items[to - 1].getBoundingClientRect().height;
    fitOne(list, page, extra + Math.max(left, lastH), tries + 1, prev);
  }
}

const focusOf = new Map<string, string>();
/** Open list `id` at the page holding its item matching `sel`, once, on its next fit (a deep link). */
export function fitFocus(id: string, sel: string) { focusOf.set(id, sel); }

/** Turn to a page; set it back to the first when the list's contents change (a new filter or sort). */
export function setFitPage(id: string, p: number) { pageOf.set(id, Math.max(0, p)); }

let bound = false;
/** Pager clicks and resizes; `rerun` fits the page again. */
export function bindFitLists(rerun: () => void) {
  if (bound) return;
  bound = true;
  document.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-fit-go]');
    if (!b) return;
    const [id, n] = b.dataset.fitGo!.split(':');
    setFitPage(id, Number(n));
    rerun();
  });
  // Opening a fold (Every blow) can make the page too tall: fit again.
  document.addEventListener('toggle', () => fitLists(document.getElementById('page')), true);
  let t = 0;
  addEventListener('resize', () => { clearTimeout(t); t = window.setTimeout(rerun, 80); });
}
